/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  SimpleChanges,
  OnChanges,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  ContactDisplay,
  ContactDuplicateSearchMode,
  ContactRoleType,
  ContactType,
} from '@gh/builder-shared-data';
import { ContextRoute, getContextRouteInitData } from '@gh/common-internal-shared';
import { EnumDictionary, TextDictionary } from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  AddressAutocompleteComponent,
  AddressComponents,
} from '@gh/core-ui';
import { assign } from 'lodash';
import { Observable } from 'rxjs';
import {
  getContactName,
  getContactPhones,
  getFullContactAddress,
  CountryCodeVal,
  COUNTRY_CODES,
  getCountriesList,
  formatPhoneNumberWithCountryCode
} from '../../contact-details-util';
import { ContactDetailsBusinessService } from '../../resources/contact-details-business.service';
import {
  ContactDuplicateFilter,
  ContactDuplicateItem,
} from '../../resources/model/contact-details-common.model';
import { ContactLocation } from '../../resources/model/contact-details.model';
import { ContactDetailsModel } from '../../services/contact-details-model.service';
import { ContactDetailsCreditorCommonFormState } from '../contact-details-creditor-common-tab/contact-details-creditor-common-form.service';
import { ContactDetailsFormState } from '../contact-details-tab-container/contact-details-form.service';
import { ContactDetailsTabContainerComponent } from '../contact-details-tab-container/contact-details-tab-container.component';
import { contactDuplicatesDialog } from '../contact-duplicates-dialog/contact-duplicates-dialog.route';
import { ContactDetailsCommonFormState } from './contact-details-common-form.service';
import { REGION_CODE } from '@gh/config';
import { MatSelectChange } from '@angular/material/select';

@Component({
  standalone: false,
  selector: 'gh-contact-details-common-tab',
  templateUrl: 'contact-details-common-tab.component.html',
  styleUrls: ['contact-details-common-tab.component.scss'],
})
export class ContactDetailsCommonTabComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @ViewChild('phoneInput1', { read: ElementRef }) phoneInputElement1: ElementRef | undefined;
  @ViewChild('phoneInput2', { read: ElementRef }) phoneInputElement2: ElementRef | undefined;
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent | undefined;

  contactTypes: EnumDictionary<ContactType>;
  contactRoleTypes: EnumDictionary<ContactRoleType>;
  contactDisplay: EnumDictionary<ContactDisplay>;
  states: TextDictionary;
  countriesListPhone1: CountryCodeVal[] = [];
  countriesListPhone2: CountryCodeVal[] = [];
  showManualAddressFields = false;
  private checkInterval: any;

  constructor(private route: ContextRoute,
    private model: ContactDetailsModel,
    private userContext: BuilderUserContext,
    private contactBusiness: ContactDetailsBusinessService,
    public masterPage: ContactDetailsTabContainerComponent,
    public form: ContactDetailsCommonFormState,
    public rootForm: ContactDetailsFormState,
    private dialogService: DialogService
  ) {
  }

  get officeView(): boolean {
    return this.userContext.officeView;
  }

  get emailRequired(): boolean {
    return this.form.isEmailRequired();
  }

  get businessNameHidden(): boolean {
    return this.form.group.value.type !== 'Business';
  }

  get individualNameHidden(): boolean {
    return this.form.group.value.type !== 'Individual' && !this.form.isEmployeeAssigned;
  }

  ngOnInit(): void {
    this.countriesListPhone1 = [...getCountriesList()].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    this.countriesListPhone2 = [...getCountriesList()].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    assign(this, getContextRouteInitData(this.route));
    this.setDefaultCountryCodes();

    const hasAddressData = !!(this.form.group.get('street1')?.value ||
      this.form.group.get('street2')?.value ||
      this.form.group.get('city')?.value ||
      this.form.group.get('state')?.value ||
      this.form.group.get('postcode')?.value);
    this.showManualAddressFields = hasAddressData;
  }

  ngOnChanges(changes: SimpleChanges) {
    setTimeout(() => {
      this.formatPhoneNumber('phone1');
      this.formatPhoneNumber('phone2');
    });
  }

  private setDefaultCountryCodes(): void {
    const region = REGION_CODE.toUpperCase();
    const countryKey = COUNTRY_CODES.get(region)?.code;
    if (!this.form.group.get('phone1CountryCode')?.value) {
      this.form.group.get('phone1CountryCode')?.setValue(countryKey);
    }

    if (!this.form.group.get('phone2CountryCode')?.value) {
      this.form.group.get('phone2CountryCode')?.setValue(countryKey);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.formatPhoneNumber('phone1');
      this.formatPhoneNumber('phone2');
      this.onAddressChange();
    });

    this.checkInterval = setInterval(() => {
      this.checkForAddressFieldErrors();
    }, 200);
  }

  ngOnDestroy(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }

  private checkForAddressFieldErrors(): void {
    if (this.showManualAddressFields) {
      return;
    }

    const addressFields = ['street1', 'city', 'state', 'postcode'];
    const hasErrors = addressFields.some(field => {
      const control = this.form.group.get(field);
      return control && control.invalid && control.touched;
    });

    if (hasErrors) {
      this.showManualAddressFields = true;
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
    }
  }

  private formatPhoneNumber(field: 'phone1' | 'phone2'): void {
    const phoneControl = this.form.getControl(field);
    const phoneNumber = phoneControl.value;
    const countryCode = this.form.getControl(`${field}CountryCode`).value;

    if (phoneNumber) {
      const formattedNumber = formatPhoneNumberWithCountryCode(phoneNumber, countryCode);
      phoneControl.setValue(formattedNumber);
      return;
    }

    phoneControl.updateValueAndValidity({ emitEvent: false });
  }

  private validateAndFormatPhoneNumber(field: 'phone1' | 'phone2', inputValue: string): void {
    const phoneControl = this.form.group.get(field);
    if (phoneControl) {
      const validationResult = this.form.phoneNumberValidator(field)(phoneControl);
      if (!validationResult) {
        const countryCode = this.form.getControl(`${field}CountryCode`).value;
        const formattedNumber = formatPhoneNumberWithCountryCode(inputValue, countryCode);
        if (formattedNumber) {
          phoneControl.setValue(formattedNumber);
        }
      }
    }
  }

  onInputPhone1(event: any) {
    this.resetPhoneFieldOnEmpty(event, 'phone1');
    this.validateAndFormatPhoneNumber('phone1', event.target.value);
  }

  onInputPhone2(event: any) {
    this.resetPhoneFieldOnEmpty(event, 'phone2');
    this.validateAndFormatPhoneNumber('phone2', event.target.value);
  }

  onBlurPhone1(event: any) {
    this.resetPhoneFieldOnEmpty(event, 'phone1');
  }

  onBlurPhone2(event: any) {
    this.resetPhoneFieldOnEmpty(event, 'phone2');
  }

  onCountryChangePhone1(change: MatSelectChange): void {
    this.moveCountryToTopPhone1(change.value);
    this.formatPhoneNumber('phone1');
  }

  onCountryChangePhone2(change: MatSelectChange): void {
    this.moveCountryToTopPhone2(change.value);
    this.formatPhoneNumber('phone2');
  }

  private moveCountryToTopPhone1(code: string): void {
    const selected = this.countriesListPhone1.find(c => c.code === code);
    if (selected) {
      const others = this.countriesListPhone1
        .filter(c => c.code !== code)
        .sort((a, b) => a.name.localeCompare(b.name));

      this.countriesListPhone1 = [selected, ...others];
    } else {
      this.countriesListPhone1 = [...this.countriesListPhone1].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    }
  }

  private moveCountryToTopPhone2(code: string): void {
    const selected = this.countriesListPhone2.find(c => c.code === code);
    if (selected) {
      const others = this.countriesListPhone2
        .filter(c => c.code !== code)
        .sort((a, b) => a.name.localeCompare(b.name));

      this.countriesListPhone2 = [selected, ...others];
    } else {
      this.countriesListPhone2 = [...this.countriesListPhone2].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    }
  }

  private resetPhoneFieldOnEmpty(event: any, field: 'phone1' | 'phone2'): void {
    const value = event.target.value || '';
    const digitsOnly = value.replace(/\D/g, '');

    if (digitsOnly.length > 15) {
      event.target.value = digitsOnly.slice(0, 15);
      return;
    }

    if (digitsOnly) {
      event.target.value = digitsOnly;
      return;
    }

    const phoneControl = this.form.getControl(field);
    phoneControl.setValue(null);
    phoneControl.markAsDirty();
    phoneControl.updateValueAndValidity();
  }
  onRoleChange(type: ContactRoleType): void {
    this.form.setRoleType(type);
  }

  onNameUpdate(): void {
    const title = getContactName(this.form.value);

    this.masterPage.setTitle(title);
    this.form.setAddressee(title);
  }

  onEmailUpdate(): void {
    const creditorForm = this.rootForm.getChildForm('creditorSupplier');

    if (creditorForm) {
      (<ContactDetailsCreditorCommonFormState>creditorForm.getChildForm('common')).updateEmail();
    }
  }

  onTypeAssign(type: ContactType): void {
    this.masterPage.assignType(type);
    if (type === ContactType.Employee) {
      if (this.form.value.type) {
        this.form.setRoleType(ContactRoleType.Individual);
      }
      return;
    }
    if (this.form.value.type) {
      this.form.setRoleType(this.form.value.type);
    }
  }

  onAssignedTypeClick(type: ContactType): void {
    const initTypeValue = this.model.original.common.contactTypes.find((contactType) => contactType.type === ContactType[type]);
    if (this.model.creationMode || (!!initTypeValue && !initTypeValue.assigned)) {
      this.masterPage.unAssignType(type);
    }
  }

  showDuplicates(filter: ContactDuplicateFilter): Observable<any> {
    const common = this.form.value;
    const original: ContactDuplicateItem = {
      name: getContactName(common),
      phones: getContactPhones(common),
      address: getFullContactAddress(common),
    };

    return this.dialogService.openWithInput(contactDuplicatesDialog, {
      duplicateFilter: filter,
      original,
    }).afterClosed();
  }

  showBusinessDuplicates(): void {
    this.showDuplicates({
      contactId: this.model.contactId,
      mode: ContactDuplicateSearchMode.BusinessName,
      businessName: this.form.value.businessName,
    }).subscribe(() => this.form.confirmations.markAsApproved('skipBusinessDuplicatesCheck'));
  }

  showIndividualDuplicates(): void {
    this.showDuplicates({
      contactId: this.model.contactId,
      mode: ContactDuplicateSearchMode.IndividualName,
      firstName: this.form.value.individualName && this.form.value.individualName.firstName,
      lastName: this.form.value.individualName && this.form.value.individualName.lastName,
    }).subscribe(() => this.form.confirmations.markAsApproved('skipIndividualDuplicatesCheck'));
  }

  showPhone1Duplicates(): void {
    this.showDuplicates({
      contactId: this.model.contactId,
      mode: ContactDuplicateSearchMode.Phone1,
      phone1: this.form.value.phone1,
    }).subscribe(() => this.form.confirmations.markAsApproved('skipPhone1DuplicatesCheck'));
  }

  showPhone2Duplicates(): void {
    this.showDuplicates({
      contactId: this.model.contactId,
      mode: ContactDuplicateSearchMode.Phone2,
      phone2: this.form.value.phone2,
    }).subscribe(() => this.form.confirmations.markAsApproved('skipPhone2DuplicatesCheck'));
  }

  loadLocations(event: AutocompleteLoadMoreEvent): void {
    event.load(this.contactBusiness.findLocations(event.query));
  }

  onSelectLocation(location: ContactLocation): void {
    this.form.setLocation(location);
    setTimeout(() => {
      this.onAddressChange();
    }, 0);
  }

  onAddressChange() {
    const street1 = this.form.group.get('street1')?.value || '';
    const street2 = this.form.group.get('street2')?.value || '';
    const city = this.form.group.get('city')?.value || '';
    const state = this.form.group.get('state')?.value || '';
    const postCode = this.form.group.get('postcode')?.value || '';

    const addressParts: string[] = [];

    const streetCombined = [street2, street1].filter(Boolean).join(' ');
    if (streetCombined) addressParts.push(streetCombined);

    if (city) addressParts.push(city);
    if (state) addressParts.push(state);
    if (postCode) addressParts.push(postCode);

    const newAddress = addressParts.join(', ');

    if (this.addressAutocomplete) {
      this.addressAutocomplete.inputValue = newAddress;
    }
  }

  onAddressSelected(components: AddressComponents): void {
    const streetParts: string[] = [];
    if (components.streetNo) streetParts.push(components.streetNo);
    if (components.street) streetParts.push(components.street);
    const street1Value = streetParts.join(' ').trim();

    this.form.group.patchValue({
      street1: street1Value || '',
      city: components.city || '',
      state: components.state || '',
      postcode: components.postalCode || ''
    });

    ['street1', 'city', 'state', 'postcode'].forEach(field => {
      const control = this.form.group.get(field);
      if (control) control.markAsDirty();
    });
  }
}
